import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Lightbulb, Star, TrendingDown, TrendingUp, XCircle,
  DollarSign, AlertTriangle, Clock, Package, RefreshCw,
  ArrowRight, ChevronDown, ChevronUp, Zap, Eye, Download, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  star: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500', dot: 'bg-emerald-500' },
  plowhorse: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500', dot: 'bg-blue-500' },
  puzzle: { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500', dot: 'bg-purple-500' },
  dog: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500', dot: 'bg-red-500' },
};

const CATEGORY_ICONS = { star: TrendingUp, plowhorse: TrendingDown, puzzle: Eye, dog: XCircle };

const PRIORITY_STYLES = {
  high: 'bg-red-500/10 text-red-600 border-red-500/30',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  low: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
};

const SUGGESTION_ICONS = {
  'trending-down': TrendingDown, 'clock': Clock, 'alert-triangle': AlertTriangle, 'package': Package,
};

function MenuEngineeringTab({ data, t, lang }) {
  const [expandedCat, setExpandedCat] = useState(null);
  if (!data) return null;

  const categories = ['star', 'plowhorse', 'puzzle', 'dog'];
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 0, maximumFractionDigits: 1 });

  return (
    <div className="space-y-6" data-testid="menu-engineering-tab">
      {/* Summary Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {categories.map(cat => {
          const Icon = CATEGORY_ICONS[cat];
          const colors = CATEGORY_COLORS[cat];
          const count = data.summary.counts[cat] || 0;
          return (
            <Card key={cat} className={`border-0 shadow-sm cursor-pointer transition-all hover:scale-[1.02] ${expandedCat === cat ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setExpandedCat(expandedCat === cat ? null : cat)} data-testid={`engineering-card-${cat}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`p-2 rounded-lg ${colors.bg}`}>
                    <Icon className={`h-4 w-4 ${colors.text}`} />
                  </div>
                  <div>
                    <div className="font-bold text-lg">{count}</div>
                    <div className="text-xs text-muted-foreground">{t(`smartSuggestions.${cat}`)}</div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">{t(`smartSuggestions.${cat}Desc`)}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Metrics */}
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="text-muted-foreground">{t('smartSuggestions.avgPopularity')}: <strong>{fmt(data.summary.avg_popularity)}</strong></span>
        <span className="text-muted-foreground">{t('smartSuggestions.avgMargin')}: <strong>{fmt(data.summary.avg_margin)} {t('common.sar')}</strong></span>
      </div>

      {/* Item List by Category */}
      {categories.map(cat => {
        const items = data.items.filter(i => i.engineering_category === cat);
        if (!items.length || (expandedCat && expandedCat !== cat)) return null;
        const colors = CATEGORY_COLORS[cat];
        const Icon = CATEGORY_ICONS[cat];

        return (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
              <h3 className="font-semibold">{t(`smartSuggestions.${cat}`)} ({items.length})</h3>
            </div>
            {items.map(item => (
              <Card key={item.id} className={`border-s-4 ${colors.border} shadow-sm`} data-testid={`engineering-item-${item.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    {item.image_url ? (
                      <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-4 w-4 ${colors.text}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm">{lang === 'ar' ? item.name_ar : item.name}</span>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                        <span>{t('smartSuggestions.price')}: {fmt(item.price)} {t('common.sar')}</span>
                        <span>{t('smartSuggestions.cost')}: {fmt(item.cost_price)} {t('common.sar')}</span>
                        <span>{t('smartSuggestions.margin')}: {fmt(item.margin)} {t('common.sar')}</span>
                        <span>{t('smartSuggestions.costPct')}: {item.food_cost_pct}%</span>
                        <span>{t('smartSuggestions.qtySold')}: {item.qty_sold}</span>
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <div className="text-sm font-bold">{fmt(item.total_profit)} {t('common.sar')}</div>
                      <div className="text-[10px] text-muted-foreground">{t('smartSuggestions.margin')}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function SuggestionsTab({ data, t, lang }) {
  const [expanded, setExpanded] = useState({});

  if (!data || !data.suggestions.length) {
    return (
      <div className="text-center py-16" data-testid="suggestions-empty">
        <Lightbulb className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">{t('smartSuggestions.noSuggestions')}</p>
      </div>
    );
  }

  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const actionLabels = {
    review_pricing: t('smartSuggestions.reviewPricing'),
    promote_or_remove: t('smartSuggestions.promoteOrRemove'),
    review_expenses: t('smartSuggestions.reviewExpenses'),
    reduce_orders: t('smartSuggestions.reduceOrders'),
  };

  return (
    <div className="space-y-6" data-testid="suggestions-tab">
      {/* Summary Banner */}
      <Card className="border-0 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-primary/10">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('smartSuggestions.totalPotentialSaving')}</div>
                <div className="text-2xl font-bold">{fmt(data.summary.total_potential_saving)} {t('common.sar')}</div>
              </div>
            </div>
            <div className="flex gap-3">
              {data.summary.high_priority > 0 && (
                <Badge className="bg-red-500/10 text-red-600 border border-red-500/20">{data.summary.high_priority} {t('smartSuggestions.highPriority')}</Badge>
              )}
              {data.summary.medium_priority > 0 && (
                <Badge className="bg-amber-500/10 text-amber-600 border border-amber-500/20">{data.summary.medium_priority} {t('smartSuggestions.mediumPriority')}</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suggestion Cards */}
      {data.suggestions.map(s => {
        const Icon = SUGGESTION_ICONS[s.icon] || Lightbulb;
        const isExpanded = expanded[s.id];
        return (
          <Card key={s.id} className="border shadow-sm overflow-hidden" data-testid={`suggestion-${s.id}`}>
            <CardContent className="p-0">
              <div className="p-4 cursor-pointer" onClick={() => setExpanded(prev => ({ ...prev, [s.id]: !prev[s.id] }))}>
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl ${PRIORITY_STYLES[s.priority]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{lang === 'ar' ? s.title_ar : s.title_en}</h3>
                      <Badge variant="outline" className={`text-[10px] ${PRIORITY_STYLES[s.priority]}`}>
                        {t(`smartSuggestions.${s.priority}Priority`)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{lang === 'ar' ? s.description_ar : s.description_en}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-end">
                      <div className="text-sm font-bold text-emerald-600">{fmt(s.potential_monthly_saving)} {t('common.sar')}</div>
                      <div className="text-[10px] text-muted-foreground">{t('smartSuggestions.potentialSaving')}</div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </div>
              </div>

              {isExpanded && s.items && (
                <div className="border-t bg-muted/30 p-4 space-y-2">
                  {s.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-dashed last:border-0">
                      <span className="font-medium">{item.name}</span>
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        {item.cost_pct && <span>{t('smartSuggestions.costPct')}: {item.cost_pct}%</span>}
                        {item.potential_saving && <span className="text-emerald-600">-{fmt(item.potential_saving)} {t('common.sar')}</span>}
                        {item.qty_sold !== undefined && <span>{t('smartSuggestions.qtySold')}: {item.qty_sold}</span>}
                        {item.growth_pct && <span className="text-red-600">+{item.growth_pct}%</span>}
                        {item.stock_cost && <span>{fmt(item.stock_cost)} {t('common.sar')}</span>}
                      </div>
                    </div>
                  ))}
                  <div className="pt-2">
                    <Badge className="bg-primary/10 text-primary border-0 gap-1">
                      <Zap className="h-3 w-3" />{t('smartSuggestions.action')}: {actionLabels[s.action] || s.action}
                    </Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function SmartSuggestionsPage() {
  const { activeBranch, authHeaders } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [tab, setTab] = useState('suggestions');
  const [engineering, setEngineering] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const [engRes, sugRes] = await Promise.all([
        axios.get(`${API}/api/smart-suggestions/menu-engineering?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/smart-suggestions?branch_id=${activeBranch.id}`, authHeaders()),
      ]);
      setEngineering(engRes.data);
      setSuggestions(sugRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeBranch, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadPDF = async () => {
    if (!activeBranch) return;
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/api/smart-suggestions/export-pdf?branch_id=${activeBranch.id}&report_type=all`,
        { ...authHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `zouqai_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('smartSuggestions.pdfDownloaded'));
    } catch { toast.error(t('common.failed')); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <ModuleLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="smart-suggestions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Lightbulb className="h-6 w-6 text-primary" />
              {t('smartSuggestions.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('smartSuggestions.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadPDF} disabled={downloading} data-testid="download-pdf-btn"
              className="gap-1.5 border-primary/30 text-primary hover:bg-primary/10">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t('smartSuggestions.downloadPdf')}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="suggestions-refresh">
              <RefreshCw className="h-4 w-4 me-1" />{t('common.update')}
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="suggestions-tabs">
            <TabsTrigger value="suggestions" className="gap-1.5">
              <Lightbulb className="h-4 w-4" />{t('smartSuggestions.costSuggestions')}
            </TabsTrigger>
            <TabsTrigger value="engineering" className="gap-1.5">
              <Star className="h-4 w-4" />{t('smartSuggestions.menuEngineering')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="suggestions">
            <SuggestionsTab data={suggestions} t={t} lang={lang} />
          </TabsContent>
          <TabsContent value="engineering">
            <MenuEngineeringTab data={engineering} t={t} lang={lang} />
          </TabsContent>
        </Tabs>
      </div>
    </ModuleLayout>
  );
}
